import { useMemo, useState } from 'react';
import { AlertTriangle, KeyRound, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { CryptoPrivacyBanner } from '../components/CryptoPrivacyBanner';
import { ToolHeader } from '../components/ToolHeader';
import { decodeJwt, formatJwtTime, type DecodedJwt } from '../lib/jwt';

type DecodeResult =
  | { ok: true; data: DecodedJwt }
  | { ok: false; error: string };

const TIME_FIELDS = new Set(['exp', 'iat', 'nbf', 'auth_time']);

/**
 * `/tools/crypto/jwt-decode` — header / payload / signature decoder.
 *
 * Hard line: this page only **decodes**, never verifies. The yellow
 * banner at the top makes that explicit, and the algorithm name from
 * the header is rendered as plain text without any "validated"
 * affordance. Verification will be a separate `jwt-sign` tool that
 * takes a secret / public key.
 */
export default function JwtDecodeTool(): JSX.Element {
  const [input, setInput] = useState('');

  const result = useMemo<DecodeResult | null>(() => {
    if (input.trim().length === 0) return null;
    try {
      return { ok: true, data: decodeJwt(input) };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
  }, [input]);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <ToolHeader
        toolId="jwt-decode"
        category="crypto"
        description="拆 JWS 紧凑序列化 → header / payload / signature。仅解码，不验证签名。"
      />

      <CryptoPrivacyBanner />

      <NotVerifiedNotice />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center gap-2 text-sm font-medium text-ink">
          <KeyRound className="h-4 w-4" />
          JWT 输入
        </header>
        <textarea
          className="input min-h-[88px] font-mono text-[12px]"
          placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOi..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
        />
        {result && !result.ok && (
          <p className="inline-flex items-center gap-1 text-sm text-rose-600 dark:text-rose-400">
            <XCircle className="h-3.5 w-3.5" />
            {result.error}
          </p>
        )}
        {result && result.ok && result.data.warnings.length > 0 && (
          <ul className="text-[12px] text-amber-700 dark:text-amber-300">
            {result.data.warnings.map((w) => (
              <li key={w} className="inline-flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> {w}
              </li>
            ))}
          </ul>
        )}
      </section>

      {result && result.ok && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <SegmentBlock title="Header" segment={result.data.header} />
          <SegmentBlock title="Payload" segment={result.data.payload} highlightTimes />
          <SignatureBlock signatureHex={result.data.signatureHex} className="lg:col-span-2" />
        </div>
      )}
    </div>
  );
}

function NotVerifiedNotice(): JSX.Element {
  return (
    <aside className="flex items-start gap-2 rounded-md border border-amber-300/40 bg-amber-50/50 px-3 py-2 text-[12px] text-amber-900 dark:border-amber-700/40 dark:bg-amber-950/40 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="leading-relaxed">
        本工具<strong className="font-semibold"> 仅解码 </strong>JWT
        —— 不会拉取 / 验证签名。任何 JWT 都能被解开，请勿将"解码成功"等同于"令牌可信"。
      </span>
    </aside>
  );
}

function SegmentBlock({
  title,
  segment,
  highlightTimes,
}: {
  title: string;
  segment: unknown;
  highlightTimes?: boolean;
}): JSX.Element {
  const json = useMemo(() => safePrettyJson(segment), [segment]);

  // Surface human-readable timestamps for the canonical JWT claim
  // names — saves users from cross-referencing a Unix epoch tool.
  const timeRows = useMemo(() => {
    if (!highlightTimes || typeof segment !== 'object' || segment === null) return [];
    const rows: Array<{ key: string; raw: unknown; human: string }> = [];
    for (const key of Object.keys(segment as Record<string, unknown>)) {
      if (!TIME_FIELDS.has(key)) continue;
      const raw = (segment as Record<string, unknown>)[key];
      const human = formatJwtTime(raw);
      if (human) rows.push({ key, raw, human });
    }
    return rows;
  }, [segment, highlightTimes]);

  return (
    <section className="card-pad flex min-h-[160px] flex-col gap-2">
      <header className="flex items-center justify-between text-sm font-medium text-ink">
        <span>{title}</span>
        <CopyButton value={json} />
      </header>
      <pre className="overflow-x-auto rounded bg-bg-soft px-2 py-1 font-mono text-[12px] text-ink">
        {json}
      </pre>
      {timeRows.length > 0 && (
        <ul className="text-[12px] text-ink-muted">
          {timeRows.map(({ key, raw, human }) => (
            <li key={key}>
              <code className="font-mono text-accent">{key}</code> = {String(raw)} →{' '}
              <span className="text-ink">{human}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SignatureBlock({
  signatureHex,
  className,
}: {
  signatureHex: string;
  className?: string;
}): JSX.Element {
  return (
    <section className={clsx('card-pad flex flex-col gap-2', className)}>
      <header className="flex items-center justify-between text-sm font-medium text-ink">
        <span>Signature (hex)</span>
        {signatureHex && <CopyButton value={signatureHex} />}
      </header>
      {signatureHex ? (
        <pre className="overflow-x-auto break-all rounded bg-bg-soft px-2 py-1 font-mono text-[12px] text-ink">
          {signatureHex}
        </pre>
      ) : (
        <p className="text-[12px] text-ink-dim">— 此 JWT 没有 signature 段（unsigned token） —</p>
      )}
    </section>
  );
}

function safePrettyJson(value: unknown): string {
  // Strings come back from `decodeJwt` when a segment failed to JSON.parse.
  // Render those as-is so the user sees what's actually there.
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function CopyButton({ value }: { value: string }): JSX.Element {
  const [done, setDone] = useState(false);
  const onClick = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 1000);
    } catch {
      /* clipboard blocked — silently ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded px-2 py-0.5 text-[11px] text-ink-muted hover:bg-bg-soft hover:text-accent"
    >
      {done ? '已复制' : '复制'}
    </button>
  );
}
